import React, { useState, useEffect, useCallback } from "react";
import DataFetchHookV3 from "../../helper/DataFetchHookV3";
import { Table, PageHeader, Card, Form, Select, Row, Button, Tooltip, Col, Input, Divider, Space, Checkbox, Modal } from "antd";
import { SearchOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";

import CustomEmptyTextProps from "../../helper/Table/CustomEmptyTextProps";
import ExportModalV3 from "../../helper/ExportModalV3";

import axios from "axios";
import moment from "moment";
import { API_TXALERT } from "../../config/config";
import { API_MERCHANT, API_RETAILER, API_SITE, API_DATA_SOURCE } from "../../config/config";
import CustomPaginationV2 from "../../helper/Table/CustomPaginationV2";
import CustomDateRangeV3 from "../../helper/Table/CustomDateRangeV3";
import ColumnResizable from "../../helper/Table/ColumnResizable";
import GroupAccess from "./../../helper/GroupAccess";
import htmlParser from "html-react-parser";
import BatchCreditDocumentUpload from "./BatchCreditDocumentUpload";
import Cookies from "js-cookie";
import CreditDocumentTooltip from "../../helper/CreditDocumentTooltip";
import TextSorter from "../../helper/Table/TextSorter";
import createExportDateHandler from "../../helper/createExportDateHandler";
import AlertCreditDocuments from "./AlertCreditDocuments";
import AlertCreditDocumentUpload from "./AlertCreditDocumentUpload";
import ProviderList from "../../components/ProviderList";

const { Option } = Select;

const DATA_URL = API_TXALERT + "/credit";
const DATA_URL_EXPORT = API_TXALERT + "/credit/reports";

const MERCHANT_DATA_URL = API_MERCHANT + "/list";
const RETAILER_DATA_URL = API_RETAILER + "/list";
const SITE_DATA_URL = API_SITE + "/list";
const RESELLER_DATA_URL = API_DATA_SOURCE + "/reseller";

const MAX_DATERANGE = 3;
const DEFAULT_DATE_RANGE = 7;
const DEFAULT_DATE_PERIOD = "days";
let START_DATE = moment().subtract(DEFAULT_DATE_RANGE, DEFAULT_DATE_PERIOD);
let END_DATE = moment();


const CreditReport = (props) => {

    //eslint-disable-next-line
    const [{ data, totalResult, isLoading, requestData, isUpdated }, doFetch, setRequestData] = DataFetchHookV3(DATA_URL, "POST", {
        startDate: START_DATE,
        endDate: END_DATE,
        page: 1,
        size: 20,
        order: "DESC",
        sort_field: "id",
    }); // eslint-disable-line

    const [dates, setDates] = useState([START_DATE, END_DATE]);
    const [formData, setFormData] = useState();
    const [currentPage, setCurrentPage] = useState(1);
    const { t, theme } = props;
    const [merchantList, setMerchantList] = useState([]);
    const [retailerList, setRetailerList] = useState([]);
    const [siteList, setSiteList] = useState([]);
    const [resellerList, setResellerList] = useState([]);
    const [inactiveChecked, setInactiveChecked] = useState(false);
    const [uploadAlertId, setUploadAlertId] = useState(null); // single shared upload modal — avoids mounting one instance per mandated row
    const [isDateExceeded, setIsDateExceeded] = useState(false);
    const [isDuplicate, setIsDuplicate] = useState(false);

    const refreshData = useCallback(() => {
        setRequestData((prev) => ({ ...prev }));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const initFormData = {
        dateRange: [START_DATE, END_DATE],
        action: [],
        outcome: [],
        keyword: "",
        credit_status: [],
        field: null,
        order: "DESC",
        merchant: [],
        retailer:[],
        site:[],
        sort_field: "id",
        status: [],
        reason: [],
        reseller: [],
    };

    const [form] = Form.useForm();

    const getResellerList = useCallback(() => {
        axios
            .post(
                RESELLER_DATA_URL,
                {},
                { headers: { Authorization: "Bearer " + Cookies.get("access_token"), Authuser: Cookies.get("authuser") } }
            )
            .then(async ({ data }) => {
                await setResellerList(data);
            })
            .catch(() => false);
    }, []);

    const getMerchantList = useCallback(() => {
        axios
            .post(
                MERCHANT_DATA_URL,
                { getall: true },
                { headers: { Authorization: "Bearer " + Cookies.get("access_token"), Authuser: Cookies.get("authuser") } }
            )
            .then(async ({ data }) => {
                await setMerchantList(data.list);
            })
            .catch(() => false);
    }, []);

    const getRetailerList = useCallback((mid_change) => {
        axios
            .post(
                RETAILER_DATA_URL,
                { mid: mid_change },
                { headers: { Authorization: "Bearer " + Cookies.get("access_token"), Authuser: Cookies.get("authuser") } }
            )
            .then(async ({ data }) => {
                await setRetailerList(data.list);
            })
            .catch(() => false);
    }, []);

    const getSiteList = useCallback((mid_change, rid_change) => {
        axios
            .post(
                SITE_DATA_URL,
                { mid: mid_change, rid: rid_change },
                { headers: { Authorization: "Bearer " + Cookies.get("access_token"), Authuser: Cookies.get("authuser") } }
            )
            .then(async ({ data }) => {
                await setSiteList(data.list);
            })
            .catch(() => false);
    }, []);

    useEffect(() => {
        formValInit();
        if (merchantList.length === 0) {
            getMerchantList();
        }
        if (retailerList.length === 0) {
            getRetailerList();
        }
        if (siteList.length === 0) {
            getSiteList();
        }
        if (resellerList.length === 0) {
            getResellerList();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    const handleChange = (pagination, filters, sorter, { currentDataSource }) => {
        var orderBy = 'ASC';
        if (sorter.order === 'descend') {
            orderBy = 'DESC';
        }
        var sortField = 'id';
        if (sorter.field !== undefined) {
            sortField = sorter.field;
        }
        var p = pagination.current;
        if (Object.keys(sorter).length > 0 && requestData.sort_field !== sortField) {
            p = 1;
            setCurrentPage(1);
        }
        setRequestData({ ...requestData, order: orderBy, sort_field: sortField, page: p, size: pagination.pageSize });
    };

    const formValInit = () => {
        setFormData({ ...initFormData });
        setIsDuplicate(false);
        form.setFieldsValue({ ...initFormData });
    };

    const clearAll = () => {
        setCurrentPage(1);
        setFormData(initFormData);
        setDates([START_DATE, END_DATE]);
        setIsDuplicate(false);
        setRequestData({ ...requestData, ...initFormData, is_duplicate: 0, startDate: START_DATE, endDate: END_DATE, page: 1 });
        form.setFieldsValue(initFormData);
    };

    const formValChange = (chgV, allV) => {
        if (["provider", "alert_id", "provider_reference"].includes(chgV?.field)) {
            form.setFieldsValue({ keyword: undefined });
        }
        setFormData(allV);
    };


    let mid_select = null;
    let rid_select = null;
    const onChangeMid = (mid_change) => {
        mid_select = mid_change
        getRetailerList(mid_select);
        getSiteList(mid_select, rid_select);
    }

    const onChangeRid = (rid_change) => {
        rid_select = rid_change
        getSiteList(mid_select, rid_select);
    }

    const onFinish = () => {

        let startDate = null;
        let endDate = null;
        let searchData = {};
        if (formData) {
            if (dates) {
                startDate = dates[0] ? dates[0] : null;
                endDate = dates[1] ? dates[1] : null;
            }
            searchData = {
                field: formData.field,
                keyword: formData.keyword ? formData.keyword.toString().trim() : null,
                order: formData.order ? formData.order : 'ASC',
                credit_status: formData.credit_status ?? [],
                mid: formData.merchant,
                rid: formData.retailer,
                sid: formData.site,
                status: formData.status,
                reason: formData.reason,
                is_duplicate: isDuplicate ? 1 : 0,
            };
            if (GroupAccess("SYSADMIN")) {
                searchData = { ...searchData, reseller: formData.reseller }
            }
        }

        setCurrentPage(1);
        setRequestData({ ...requestData, ...searchData, startDate: startDate, endDate: endDate, page: 1 });
    };

    const customLocale = {
        emptyText: CustomEmptyTextProps(
            <>
                {t("tables.emptytext") + " "}
                <span style={{ cursor: "pointer", color: theme }} onClick={clearAll}>
                    {t("tables.actions.clear_filters")}
                </span>
            </>
        ),
    };

    const customLoading = {
        emptyText: CustomEmptyTextProps(
            <>
                <span>{t("tables.loading")}</span>
            </>,
            isLoading
        ),
    };

    const exportHeaders = [
        { label: t("tables.fields.alertdate"), key: 'alertdate' },
        { label: t("tables.fields.alertid"), key: 'id' },
        { label: t("tables.fields.providername"), key: 'providername' },
        { label: t("tables.fields.providerreference"), key: 'provider_reference' },
        { label: t("tables.fields.credit_status"), key: 'credit_status' },
        { label: t("tables.fields.status_update_date"), key: 'status_update_date' },
        { label: t("tables.fields.status_reason"), key: 'credit_status_reason' },
        { label: t("tables.fields.is_duplicate"), key: 'is_duplicate' },
        { label: t("tables.fields.comment"), key: 'comment' },
        { label: t("tables.fields.documents"), key: 'num_documents' },
    ];

    const viewCreditDocument = (record) => {
        Modal.info({
            title: ` Alert ${record.id} Documents `,
            width: 600,
            icon: null,
            content: <AlertCreditDocuments t={t} alertid={record.id} callback={refreshData}/>,
        });
    }

    const [columns, setColumns] = useState([
        {
            dataIndex: "id",
            key: "id",
            title: t("tables.fields.alertid"),
            width: 20,
            render: (id) => (
                <a href={`/alert/${id}`} target="_blank" rel="noopener noreferrer">{id}</a>
            ),
            sorter: {
                compare: (a, b) => a.id - b.id,
            },
        },
        {
            dataIndex: "alertdate",
            key: "alertdate",
            title: t("tables.fields.alertdate"),
            width: 20,
            render: (datecreated) => moment(datecreated).format("lll"),
            sorter: {
                compare: (a, b) => moment(a.alertdate).diff(moment(b.alertdate)),
            },
        },
        {
            dataIndex: "providername",
            key: "providername",
            title: t("tables.fields.providername"),
            width: 20,
            sorter: {
                compare: (a, b) => TextSorter(a.providername,b.providername),
            },
        },
        {
            dataIndex: "credit_status",
            key: "credit_status",
            title: t("tables.fields.credit_status"),
            width: 20,
            render: (credit_status) => {
                if (!credit_status) {
                    return null;
                }

                switch (credit_status.toLowerCase()) {
                    case 'approved':
                        return (
                            <>
                                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18, marginRight: 4 }} />
                                {t("contents.credit_report.approved")}
                            </>
                        );
                    case 'declined':
                        return (
                            <>
                                <CloseCircleOutlined style={{ color: "#f5222d", fontSize: 18, marginRight: 4 }} />
                                {t("contents.credit_report.declined")}
                            </>
                        );
                    case 'requested':
                        return (
                            <>
                                <ClockCircleOutlined style={{ color: "#faad14", fontSize: 18, marginRight: 4 }} />
                                {t("contents.credit_report.requested")}
                            </>
                        );
                    case 'new':
                        return (
                            <>
                                <InfoCircleOutlined style={{ color: "#1890ff", fontSize: 18, marginRight: 4 }} />
                                {t("contents.credit_report.new")}
                            </>
                        );
                    default:
                        return credit_status;
                }
            },
            sorter: {
                compare: (a, b) => TextSorter(a.credit_status ?? "", b.credit_status ?? ""),
            },
        },
        {
            dataIndex: "status_update_date",
            key: "status_update_date",
            title: t("tables.fields.status_update_date"),
            width: 20,
            render: (status_update_date) => status_update_date ? moment(status_update_date).format("lll") : "-",
            sorter: {
                compare: (a, b) => (a.status_update_date ? moment(a.status_update_date).unix() : 0) - (b.status_update_date ? moment(b.status_update_date).unix() : 0),
            },
        },
        {
            dataIndex: "credit_status_reason",
            key: "credit_status_reason",
            title: t("tables.fields.status_reason"),
            width: 20,
            sorter: {
                compare: (a, b) => TextSorter(a.credit_status_reason,b.credit_status_reason),
            },
        },
        {
            dataIndex: "num_documents",
            key: "num_documents",
            title: t("tables.fields.documents"),
            width: 20,
            sorter: {
                compare: (a, b) => (a.num_documents ?? 0) - (b.num_documents ?? 0),
            },
            render: (value, record) => {
                let numOfDoc = record.num_documents;
                if (numOfDoc > 0) {
                    return (
                        <>
                            {numOfDoc}
                            <Button size="small" type="link" style={{ marginLeft: "5px" }} onClick={() => viewCreditDocument(record)}>
                                {t("tables.actions.manage") || "Manage"}
                            </Button>
                        </>
                    );
                }
                return (
                    <>
                        {numOfDoc ?? 0}
                        <Button size="small" type="link" style={{ marginLeft: "5px" }} onClick={() => setUploadAlertId(record.id)}>
                            {t("tables.actions.upload") || "Upload"}
                        </Button>
                    </>
                );
            }
        },
    ]);

    const getLocale = () => {
        if (isLoading) {
            return customLoading;
        } else if (data.datalist) {
            return customLocale;
        } else {
            return {};
        }
    };

    const onChangeInactive = (e) => {
        setInactiveChecked(e.target.checked);
    };

    const getMerchant = () => {
        if (inactiveChecked) {
            return merchantList.map((item) => (
                item.status === 'active' ?
                    <Option key={item.mid} value={item.mid}>
                        {htmlParser(item.name) + ` (mid: ` + item.mid + `)`}
                    </Option>
                    : <Option key={item.mid} value={item.mid} style={{ color: "#f5222d" }}>
                        {htmlParser(item.name) + ` (mid: ` + item.mid + `)`}
                    </Option>
            ))
        } else {
            return merchantList.map((item) => (
                item.status === 'active'
                    ?
                    <Option key={item.mid} value={item.mid}>
                        {htmlParser(item.name) + ` (mid: ` + item.mid + `)`}
                    </Option>
                    :
                    null
            ))
        }
    };

    /**
     * callback to reflect the changes on dates to the passing request data
     * 
     * @param array values 
     */
    const handleDateChange = createExportDateHandler(requestData);

    const AdvancedSearchForm = () => (
        <div className="search-wrap">
            <Card title={t("tables.actions.search")} size="small">
                <Form form={form} size="large" name="advanced_search" className="ant-advanced-search-form" onValuesChange={formValChange} onFinish={onFinish}>
                    <Row gutter={[16, 16]}>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Tooltip title="Filter dates will be applied on 'Status Update Date'">
                                <Form.Item name="dateRange"
                                    rules={[
                                        {
                                            validator: () => {
                                                if (isDateExceeded) {
                                                    return Promise.reject(t('max_3_months'));
                                                } else if ((dates[0] && !dates[1]) || (!dates[0] && dates[1])) {
                                                    return Promise.reject(t('min_1_day'));
                                                } else {
                                                    return Promise.resolve();
                                                }
                                            }
                                        }
                                    ]}
                                >
                                    <CustomDateRangeV3 t={t} setIsDateExceeded={setIsDateExceeded} state={{ dates, setDates }} unit="months" limit={MAX_DATERANGE} onChange={handleDateChange} />
                                </Form.Item>
                            </Tooltip>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}></Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}></Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}></Col>

                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="merchant">
                                <Select
                                    showSearch
                                    mode="multiple"
                                    showArrow="true"
                                    maxTagCount="responsive"
                                    style={{ width: "100%" }}
                                    optionFilterProp="children"
                                    onChange={onChangeMid}
                                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                    filterSort={(optionA, optionB) => optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())}
                                    placeholder={t("contents.merchants.title")}
                                    dropdownRender={(menu) => GroupAccess("SYSADMIN") ? (
                                        <>
                                            {menu}

                                            <Divider
                                                style={{
                                                    margin: '8px 0',
                                                }}
                                            />
                                            <Space
                                                style={{
                                                    padding: '0 8px 4px',
                                                }}
                                            >
                                                <Checkbox onChange={onChangeInactive}>Show Inactive?</Checkbox>
                                            </Space>
                                        </>
                                    ) : (
                                        <>
                                            {menu}
                                        </>
                                    )}
                                >
                                    {merchantList
                                        ? getMerchant()
                                        : null}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="retailer">
                                <Select
                                    showSearch
                                    mode="multiple"
                                    showArrow="true"
                                    maxTagCount="responsive"
                                    style={{ width: "100%" }}
                                    optionFilterProp="children"
                                    onChange={onChangeRid}
                                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                    filterSort={(optionA, optionB) => optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())}
                                    placeholder={t("contents.retailers.title")}>
                                    {retailerList
                                        ? retailerList.map((item) => (
                                            <Option key={item.rid} value={item.rid}>
                                                {htmlParser(item.name) + ` (rid: ` + item.rid + `)`}
                                            </Option>
                                        ))
                                        : null}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="site">
                                <Select
                                    showSearch
                                    mode="multiple"
                                    maxTagCount="responsive"
                                    showArrow="true"
                                    style={{ width: "100%" }}
                                    optionFilterProp="children"
                                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                                    filterSort={(optionA, optionB) => optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())}
                                    placeholder={t("contents.sites.title")}>
                                    {siteList
                                        ? siteList.map((item) => (
                                            <Option key={item.sid} value={item.sid}>
                                                {htmlParser(item.name) + ` (sid: ` + item.sid + `)`}
                                            </Option>
                                        ))
                                        : null}
                                </Select>
                            </Form.Item>
                        </Col>
                        {GroupAccess("SYSADMIN") ? (
                            <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                                <Form.Item name="reseller">
                                    <Select
                                        showSearch
                                        mode="multiple"
                                        showArrow="true"
                                        maxTagCount="responsive"
                                        style={{ width: "100%" }}
                                        optionFilterProp="children"
                                        placeholder={t("contents.resellers.title")}
                                    >
                                        {resellerList
                                            ? resellerList.map((item) => (
                                                <Option key={item.reseller_id} value={item.reseller_id}>
                                                    {item.reseller_id + ` (reseller: ` + item.name + `)`}
                                                </Option>
                                            ))
                                            : null}
                                    </Select>
                                </Form.Item>
                            </Col>
                        ) : (
                            ""
                        )}

                         <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="credit_status">
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    mode="multiple"
                                    allowClear
                                    style={{ width: "100%" }}
                                    placeholder={t("tables.fields.credit_status")}>
                                    <Option value="NEW">{t("contents.credit_report.new")}</Option>
                                    <Option value="APPROVED">{t("contents.credit_report.approved")}</Option>
                                    <Option value="DECLINED">{t("contents.credit_report.declined")}</Option>
                                    <Option value="REQUESTED">{t("contents.credit_report.requested")}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="field">
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    style={{ width: "100%" }}
                                    placeholder={t("tables.fields.field")}>
                                    <Option value="alert_id">{t("contents.alerts.alert_id")}</Option>
                                    <Option value="provider">{t("tables.fields.providername")}</Option>
                                    <Option value="provider_reference">{t("tables.fields.providerreference")}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }}>
                            <Form.Item name="keyword">
                                {form.getFieldValue("field") === "provider" ? (
                                    <ProviderList mode={undefined} allowClear style={{ width: "100%" }} placeholder={t("tables.fields.providername")} />
                                    ):
                                    <Input placeholder={t("tables.fields.keyword")} />
                                }
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 12 }} xl={{ span: 12 }} >
                            <Form.Item>
                                <span style={{ marginRight: 8 }}>
                                    {t("tables.fields.show")}:
                                </span>
                                <Checkbox checked={isDuplicate} onChange={(e) => setIsDuplicate(e.target.checked)}>
                                     {t("tables.fields.is_duplicate")}
                                </Checkbox>
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 12 }}
                            md={{ span: 6, offset: 12 }}
                            lg={{ span: 6, offset: 12 }}
                            xl={{ span: 3, offset: 17 }}
                        >
                            <Form.Item>
                                <Button type="secondary" onClick={() => clearAll()} block>
                                    {t("tables.actions.clear_all")}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 4 }}>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" block>
                                    <SearchOutlined /> {t("tables.actions.search")}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );

    const getDataSource = (datalist) => (datalist && Array.isArray(datalist) ? datalist : []);

    const getHeaderTable = () => (
        <div className="custom-table--header d-flex justify-content-between align-items-center">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 className="table-title">{t("contents.credit_report.title")}</h3>
                <BatchCreditDocumentUpload t={t} onUploadSuccess={refreshData} />
                <Tooltip title={<CreditDocumentTooltip t={t} />} overlayStyle={{ maxWidth: 400 }}>
                    <QuestionCircleOutlined style={{ color: "#999", cursor: "pointer" }} />
                </Tooltip>
            </div>
            <ExportModalV3
                t={t}
                export_header={true}
                keyname="export_all"
                title={t("tables.actions.export")}
                export_option={true}
                data={requestData}
                data_url={DATA_URL_EXPORT}
                headers={exportHeaders}
                fieldHeadersOptional={exportHeaders}
                filename={`alert_${moment().format("YYMMDD_HHmmss_SSSS")}.csv`}
                style={{ marginLeft: 8 }}
            />
        </div>
    );

    const mergeColumns = columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column) => ({
            width: column.width,
            onResize: (_, { size }) => {
                const newColumns = [...columns];
                newColumns[index] = { ...newColumns[index], width: size.width };
                setColumns(newColumns);
            },
        }),
    }));

    return (
        <>
            <PageHeader title={t("contents.credit_report.title")}></PageHeader>
            {AdvancedSearchForm()}
            <Table
                size="small"
                locale={getLocale()}
                loading={isLoading}
                title={() => getHeaderTable()}
                rowKey="id"
                className="table-v3 custom-pagination custom-pagination-v3"
                components={{
                    header: {
                        cell: ColumnResizable,
                    },
                }}
                columns={mergeColumns}
                scroll={{ x: true}}
                dataSource={getDataSource(data.datalist)}
                onChange={handleChange}
                sortDirections={["ascend", "descend"]}
                pagination={CustomPaginationV2({ totalResult, currentPage, requestData, setRequestData, setCurrentPage })}
            />
            {/* Mounted only when a row's Upload button is clicked; unmounts on close to reset state */}
            {uploadAlertId && (
                <AlertCreditDocumentUpload
                    forceOpen
                    t={t}
                    alertid={uploadAlertId}
                    onUploadSuccess={() => { setUploadAlertId(null); refreshData(); }}
                    onClose={() => setUploadAlertId(null)}
                />
            )}
        </>
    );
};

export default CreditReport;
